import _ from 'lodash';
import React from 'react';

import { Skeleton, TableCell, TableRow } from '@mui/material';
import { Table } from 'iron-forge-react-modules';



export function useSortedColumns<T extends string>({
  columnIdsToRender, columns
}: {
  columnIdsToRender: T[];
  columns: Table.IColumn<T>[];
}) {
  return React.useMemo(
    () => {
      const ids = new Set(columnIdsToRender);
      const columnId2Index = _.mapValues(
        _.keyBy(columnIdsToRender, c => c),
        columnId => _.indexOf(columnIdsToRender, columnId)
      );

      const columnsToRender = _.filter(columns, column => ids.has(column.id));
      return _.orderBy(columnsToRender, col => columnId2Index[col.id]);
    },
    [
      columnIdsToRender,
      columns
    ]
  );
}



export function useQueryOnFilterChange<TFilter extends object>({
  filter, fnQuery
}: {
  filter: TFilter;
  fnQuery: (args: { filter?: TFilter; }) => void;
}) {
  const [currentFilter, setCurrentFilter] = React.useState(filter);

  React.useEffect(
    () => {
      if (!_.isEqual(filter, currentFilter)) {
        setCurrentFilter({ ...filter });
        fnQuery({ filter });
      }
    },
    [
      currentFilter,
      filter,
      fnQuery
    ]
  );
}



export function useRefetchOverInterval(tuple: { refetch: () => void; }, intervalMs: number) {
  React.useEffect(() => {
    const pointer = setInterval(() => {
      tuple.refetch();
    }, intervalMs);

    return () => clearInterval(pointer);
  }, [
    intervalMs,
    tuple
  ]);
}



export function useLoadingRows<TColumnId extends string>(columns: Table.IColumn<TColumnId>[]) {
  return React.useMemo(
    () => _.times(20).map((i): Table.IRow<TColumnId> => ({
      id: i.toString(),
      columnId2Value: _.mapValues(_.keyBy(columns, col => col.id), (): Table.IRowComponentValue => ({
        type: 'component',
        sortableValue: i,
        label: <Skeleton />
      })) as Record<TColumnId, Table.IRowComponentValue>
    })),
    [columns]
  );
}



export function makeSkeletonRows(numColumns: number, numRows: number) {
  return _.times(numRows, row => (
    <TableRow key={row}>
      {_.times(numColumns, col => (
        <TableCell key={col}><Skeleton /></TableCell>
      ))}
    </TableRow>
  ));
}
